import * as React from 'react';
import { Select, Checkbox, Button, Divider, Space, type GetProp } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { PropagationTag } from '@shared/components/PropagationDisplay';
import { MetricsDimensionsPopoverButton } from './PopoverButton';
import * as Styled from './styled';
import { PROPAGATION_FULL_DISPLAY } from '@shared/constants/analysis';

type DimensionSelectOptionType = {
  label: string;
  value: string;
  defaultPropagation?: Propagation;
  predefined: boolean;
};

type DimensionSelectType = typeof Select<string, DimensionSelectOptionType>;

export interface MetricsDimensionsCustomizeProps {
  dimensionOptions?: DimensionSelectOptionType[];
  metricOptions?: GetProp<typeof Checkbox.Group<string>, 'options'>;
  stepOptions?: GetProp<typeof MetricsDimensionsPopoverButton, 'stepOptions'>;
  defaultDimensionValue?: string;
  defaultMetricsValue?: string[];
  propagationsValue?: Record<string, Propagation>;
  defaultPropagationValue?: Propagation;
  okText?: string;
  cancelText?: string;
  className?: string;
  style?: React.CSSProperties;
  onOk?: (value: { dimension: string; metrics: string[]; propagation: Propagation }) => void;
  onCancel?: () => void;
  onDimensionCreate?: () => void;
  onDimensionEdit?: (dimension: string) => void;
}

const Customize = (props: MetricsDimensionsCustomizeProps) => {
  const {
    dimensionOptions,
    metricOptions,
    stepOptions,
    defaultDimensionValue,
    defaultMetricsValue,
    propagationsValue = {},
    defaultPropagationValue = 'context',
    okText = 'Apply',
    cancelText = 'Cancel',
    onOk,
    onCancel,
    onDimensionCreate,
    onDimensionEdit,
    ...p
  } = props;
  const [selectedDimension, setSelectedDimension] = React.useState(defaultDimensionValue);
  const [selectedMetrics, setSelectedMetrics] = React.useState(defaultMetricsValue);
  const [selectedPropagation, setSelectedPropagation] = React.useState<Propagation>(() => {
    if (defaultDimensionValue) {
      return (
        propagationsValue[defaultDimensionValue] ??
        dimensionOptions?.find((it) => it.value === defaultDimensionValue)?.defaultPropagation ??
        defaultPropagationValue
      );
    }

    return defaultPropagationValue;
  });
  const [selectOpen, setSelectOpen] = React.useState<boolean | undefined>(undefined);
  const innerPopoverOpenRef = React.useRef(false);

  const selectOptionRender = React.useCallback<GetProp<DimensionSelectType, 'optionRender'>>(
    (option) => {
      const propagation =
        propagationsValue?.[option.data.value] ??
        option.data.defaultPropagation ??
        defaultPropagationValue;

      return (
        <Styled.DimensionOptionContainer>
          <div>{option.data.label}</div>
          <Space size={4}>
            <PropagationTag title={PROPAGATION_FULL_DISPLAY[propagation]}>
              {propagation}
            </PropagationTag>
            <Button
              size="small"
              disabled={option.data.predefined}
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onDimensionEdit?.(option.data.value);
              }}
            />
          </Space>
        </Styled.DimensionOptionContainer>
      );
    },
    [defaultPropagationValue, propagationsValue, onDimensionEdit],
  );
  const selectLabelRender = React.useCallback<GetProp<DimensionSelectType, 'labelRender'>>(
    (props) => (
      <Styled.DimensionOptionContainer title={PROPAGATION_FULL_DISPLAY[selectedPropagation]}>
        <div>{props.label}</div>
        {!!(props.label && props.value) && (
          <MetricsDimensionsPopoverButton
            value={selectedPropagation}
            stepOptions={stepOptions}
            buttonProps={{
              size: 'small',
              variant: 'filled',
              color: 'primary',
              title: PROPAGATION_FULL_DISPLAY[selectedPropagation],
            }}
            onChange={setSelectedPropagation}
          >
            {selectedPropagation}
          </MetricsDimensionsPopoverButton>
        )}
      </Styled.DimensionOptionContainer>
    ),
    [selectedPropagation, stepOptions],
  );
  const selectPopupRender = React.useCallback<GetProp<DimensionSelectType, 'popupRender'>>(
    (menu) => (
      <>
        {menu}
        <Divider style={{ margin: '8px 0' }} />
        <Button variant="link" color="primary" icon={<PlusOutlined />} onClick={onDimensionCreate}>
          New Dimension
        </Button>
      </>
    ),
    [onDimensionCreate],
  );
  const handleSelectOpenChange = React.useCallback<GetProp<DimensionSelectType, 'onOpenChange'>>(
    (open) => {
      if (innerPopoverOpenRef.current) return;
      setSelectOpen(open);
    },
    [],
  );
  const handleSelectChange = React.useCallback<GetProp<DimensionSelectType, 'onChange'>>(
    (value, option) => {
      setSelectedDimension(value);
      setSelectedPropagation(
        propagationsValue?.[value] ??
          (option as DimensionSelectOptionType)?.defaultPropagation ??
          defaultPropagationValue,
      );
    },
    [defaultPropagationValue, propagationsValue],
  );
  const handleOk = React.useCallback(() => {
    if (!selectedDimension || !selectedMetrics?.length) return;

    onOk?.({
      dimension: selectedDimension,
      metrics: selectedMetrics,
      propagation: selectedPropagation,
    });
  }, [onOk, selectedDimension, selectedMetrics, selectedPropagation]);

  return (
    <Styled.Container {...p}>
      <Styled.ContentContainer>
        <Styled.Label>Dimension</Styled.Label>
        <Select
          open={selectOpen}
          options={dimensionOptions}
          defaultValue={selectedDimension}
          showSearch
          optionFilterProp="label"
          popupRender={selectPopupRender}
          labelRender={selectLabelRender}
          optionRender={selectOptionRender}
          onChange={handleSelectChange}
          onOpenChange={handleSelectOpenChange}
        />
        <Styled.Label style={{ marginTop: 8 }}>Metrics</Styled.Label>
        <Checkbox.Group
          value={selectedMetrics}
          options={metricOptions}
          onChange={setSelectedMetrics}
        />
      </Styled.ContentContainer>
      <Styled.ActionContainer>
        <Button size="small" onClick={onCancel}>
          {cancelText}
        </Button>
        <Button
          size="small"
          color="primary"
          variant="outlined"
          disabled={!selectedDimension || !selectedMetrics?.length}
          onClick={handleOk}
        >
          {okText}
        </Button>
      </Styled.ActionContainer>
    </Styled.Container>
  );
};

export const MetricsDimensionsCustomize = React.memo(Customize);
